import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import {
  Dialog,
  CircleWarningIcon,
  Typography,
  Radio,
  Button,
  Skeleton,
  Input,
  Card,
  CheckBoxSkeleton
} from '@chilecompra/react-kit/components'
import {
  checkInputErrorMaxLength,
  checkInputErrorMinLength,
  checkInputErrorRequired,
  useInput,
  useRadio,
  useViewport
} from '@chilecompra/react-kit'

import {
  onCloseDeclarationInadmissibilityQuoteDialog,
  onDeclareInadmissibilityQuoteThunk,
  onGetCausesDeclarationInadmissibilityQuoteDialogThunk
} from './QuoteDeclarationInadmissibilityDialog.actions'
import {
  BoxInput,
  ContainerError,
  DialogWrapper,
  Row,
  RowRadio,
  Strong,
  ErrorIcon,
  DialogWrapperConfirmed
} from './QuoteDeclarationInadmissibilityDialog.styles'

import { DECLARATION_INADMISSIBILITY_JUSTIFICATION_DICTIONARY } from '../../config/settings/constants'
import { topPage } from '../../modules/utils/topPage'
import { inIframe } from '../../modules/utils/inIframe'

/**
 * The DeclarationInadmissibilityQuoteDialog's container.
 */
const DeclarationInadmissibilityQuoteDialog = props => {
  const { onDeclaration } = props

  const {
    open: isOpenDeclarationDialog,
    showDeclarationInadmissibility,
    quoteBid,
    justificationsCriteria,
    loading: declarationLoading
  } = useSelector(state => state.declarationInadmissibilityQuoteDialog)
  const { loading: quoteLoading, quoteBidDetail: quoteSelected } = useSelector(state => state.quoteBid)
  const { basicInfo: quoteBasicInfo } = useSelector(state => state.quoteBasicInformation)

  const dispatch = useDispatch()
  const { size } = useViewport()

  const [isDisabledInputOther, setDisabledInputOther] = useState(true)
  const [isErrorDeclaration, setErrorDeclaration] = useState(false)
  const [confirmedDeclaration, setConfirmedDeclaration] = useState(false)

  const isInIframe = useMemo(() => inIframe(), [])

  const selectionCriteriaOrdered = justificationsCriteria.sort((a, b) => {
    if (a.order === b.order) {
      return 0
    }
    return a.order > b.order ? 1 : -1
  })

  const {
    error: criteriaError,
    onChange: handleCriteriaChange,
    value: criteriaValue,
    setValue: setCriteriaValue
  } = useInput({
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorMinLength(5), checkInputErrorMaxLength(100)]
  })
  const {
    value: valueRadioSelected,
    setValue: setValueRadioSelected,
    onChange
  } = useRadio({
    changeCallback: valueRadio => {
      if (valueRadio === DECLARATION_INADMISSIBILITY_JUSTIFICATION_DICTIONARY.OTHER) {
        setDisabledInputOther(false)
        setCriteriaValue('')
      } else {
        setDisabledInputOther(true)
      }
    }
  })

  const handleOnClose = () => dispatch(onCloseDeclarationInadmissibilityQuoteDialog())
  const handleConfirm = () => {
    setErrorDeclaration(false)

    if (!valueRadioSelected) {
      setErrorDeclaration(true)
      return
    }

    if (
      valueRadioSelected === DECLARATION_INADMISSIBILITY_JUSTIFICATION_DICTIONARY.OTHER &&
      (criteriaValue.length <= 0 || criteriaError.length > 0)
    ) {
      setErrorDeclaration(true)
      return
    }

    setConfirmedDeclaration(true)
  }
  const handleDeclare = () => {
    const declaration = {
      quoteBidId: quoteBid,
      quoteId: quoteBasicInfo?.id,
      justification: '',
      type: valueRadioSelected
    }

    if (valueRadioSelected === DECLARATION_INADMISSIBILITY_JUSTIFICATION_DICTIONARY.OTHER) {
      declaration.justification = criteriaValue
    } else {
      declaration.justification =
        justificationsCriteria.find(justification => justification.value === valueRadioSelected)?.label || ''
    }

    dispatch(
      onDeclareInadmissibilityQuoteThunk({
        declaration,
        callback: onDeclaration
      })
    )
  }

  useEffect(() => {
    setErrorDeclaration(false)
    if (showDeclarationInadmissibility) {
      dispatch(onGetCausesDeclarationInadmissibilityQuoteDialogThunk())
      setValueRadioSelected('')
      setCriteriaValue('')
      setConfirmedDeclaration(false)
    }
  }, [showDeclarationInadmissibility])

  useEffect(() => {
    topPage(isInIframe, isOpenDeclarationDialog)
  }, [isOpenDeclarationDialog])

  return (
    <Dialog
      disableBackdropClick
      maxWidth={size.isTiny || size.isSmall || size.isMedium ? '700px' : '50%'}
      open={isOpenDeclarationDialog}
      verticalAlign="top"
      onClose={handleOnClose}
    >
      {!confirmedDeclaration && (
        <DialogWrapper>
          <Row margin="10px 0 0">
            {quoteLoading && <Skeleton margin="0 0 0 25px" width="85%" />}
            {!quoteLoading && (
              <>
                <CircleWarningIcon fontSize="large" />
                <Typography color="black1" fontSize="18px" fontWeight="bold" lineHeight="22px" variant="h4">
                  Declarar inadmisible cotización de {quoteSelected?.businessName}
                </Typography>
              </>
            )}
          </Row>
          <Row margin="10px 0 0">
            {quoteLoading && <Skeleton margin="0 0 0 25px" width="85%" />}
            {!quoteLoading && (
              <>
                <Typography color="black1" fontSize="14px" lineHeight="18px">
                  Ingresa el motivo para declarar inadmisible la cotización del proveedor. El motivo ingresado será
                  visible públicamente en la Ficha de Compra Ágil.
                </Typography>
                <Strong>Esta acción es irreversible.</Strong>
              </>
            )}
          </Row>

          <Row margin="10px 0 0">
            <RowRadio>
              {declarationLoading && (
                <>
                  <CheckBoxSkeleton width="85%" />
                  <CheckBoxSkeleton width="85%" />
                  <CheckBoxSkeleton width="85%" />
                  <CheckBoxSkeleton width="85%" />
                  <CheckBoxSkeleton width="85%" />
                </>
              )}
              {!declarationLoading && (
                <>
                  <Radio onChange={onChange} options={selectionCriteriaOrdered} value={valueRadioSelected} />
                  {valueRadioSelected === DECLARATION_INADMISSIBILITY_JUSTIFICATION_DICTIONARY.OTHER && (
                    <BoxInput>
                      <Input
                        error={
                          valueRadioSelected === DECLARATION_INADMISSIBILITY_JUSTIFICATION_DICTIONARY.OTHER &&
                          criteriaError
                        }
                        disabled={isDisabledInputOther}
                        maxCount={100}
                        onChange={handleCriteriaChange}
                        type="text"
                        value={
                          valueRadioSelected === DECLARATION_INADMISSIBILITY_JUSTIFICATION_DICTIONARY.OTHER
                            ? criteriaValue
                            : ''
                        }
                      />
                    </BoxInput>
                  )}
                </>
              )}
            </RowRadio>
          </Row>

          {isErrorDeclaration && (
            <Row>
              <Card color="error" padding="8px">
                <ContainerError>
                  <ErrorIcon fontSize="small" />
                  <Typography color="black2" fontSize="14px" margin="0 0 1px 14px">
                    Selecciona una opción para poder continuar.
                  </Typography>
                </ContainerError>
              </Card>
            </Row>
          )}

          <Row margin="10px 0 0">
            <Button color="error" type="button" onClick={handleConfirm} loading={declarationLoading}>
              Declarar inadmisible
            </Button>
          </Row>
          <Row margin="30px 0 0">
            <Button variant="text" onClick={handleOnClose}>
              Volver
            </Button>
          </Row>
        </DialogWrapper>
      )}

      {confirmedDeclaration && (
        <DialogWrapperConfirmed>
          <Row margin="0 10px" width="75%">
            <CircleWarningIcon fontSize="large" />
            <Typography color="black1" fontSize="18px" fontWeight="bold" lineHeight="22px" variant="h4">
              Estás a punto de declara inadmisible a la empresa proveedora {quoteSelected?.businessName}
            </Typography>
          </Row>
          <Row margin="10px 0 30px" width="75%">
            <Typography color="black1" fontSize="14px" lineHeight="18px">
              Al declararla inadmisible, no podrás volver a seleccionar esta cotización. El motivo ingresado será
              visible públicamente en la Ficha de Compra Ágil.
            </Typography>
            <Strong>Esta acción es irreversible.</Strong>
          </Row>

          <Row margin="10px 0 0">
            <Button color="error" type="button" onClick={handleDeclare} loading={declarationLoading}>
              Continuar y declarar inadmisible cotización
            </Button>
          </Row>
          <Row margin="30px 0 0">
            <Button variant="outline" onClick={handleOnClose}>
              Cancelar y volver
            </Button>
          </Row>
        </DialogWrapperConfirmed>
      )}
    </Dialog>
  )
}

DeclarationInadmissibilityQuoteDialog.propTypes = {
  onDeclaration: PropTypes.func.isRequired
}

export default DeclarationInadmissibilityQuoteDialog
